/**
 * The coordinate panel: `/map?pick`.
 *
 * A build tool that happens to live in the app. Two things about this plan can
 * only come from a person — where a venue pin goes, and which buildings make up
 * a wing — and this is where you work them out.
 *
 * IT TAKES TYPED COORDINATES, not taps, and that is a deliberate second
 * attempt. The first version picked on tap, which sounds better and is worse:
 * you cannot nudge a tap, you cannot repeat one exactly, and on a plan where
 * the accommodation blocks are eight metres apart the difference between the
 * right building and its neighbour is a few pixels. Typing 30.7 and then 30.9
 * is precise, repeatable, and reads back the same numbers that go into
 * `venue.json`. Tapping still works and fills the boxes in, but it is the
 * coarse gesture that gets you close, not the mechanism.
 *
 * The numbers ARE the stored format: percentages of the plan, 0-100 on both
 * axes. x runs along the beach (0 south, 100 north), y runs inland (0 out at
 * sea, 100 at the back fence). `assets/map/coordinate-grid.png` is the same
 * grid on paper, for working offline.
 *
 * NOT GATED, AND THAT IS DELIBERATE. It reads nothing, writes nothing and needs
 * no permission — it is arithmetic on geometry already in the bundle. Hiding it
 * behind a build flag would lock out the person who most needs it: someone
 * standing in the resort with a phone. A guest will not type `?pick`.
 */
import { useState } from 'react';
import { buildingAt, planFromPercent, type PlanPercent } from '../lib/geo';
import { VIEW } from '../data/resort-plan';

export interface Pick {
  pct: PlanPercent;
  /** Plan metres — a difference in metres is something you can pace out on the
   *  ground, unlike a difference in percent. */
  metres: { x: number; y: number };
  buildingId: number | null;
  buildingName: string | null;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/** Everything the content file needs, for one point on the plan. */
export function pickAt(pct: PlanPercent): Pick {
  const clamped = {
    x: Math.min(100, Math.max(0, pct.x)),
    y: Math.min(100, Math.max(0, pct.y)),
  };
  const plan = planFromPercent(clamped);
  const hit = buildingAt(plan);
  return {
    pct: { x: round1(clamped.x), y: round1(clamped.y) },
    metres: { x: Math.round(plan.x), y: Math.round(plan.y) },
    buildingId: hit?.id ?? null,
    buildingName: hit?.name ?? null,
  };
}

interface Props {
  pick: Pick;
  onPick: (p: Pick) => void;
  /** Ids collected so far, lifted into MapPage so the plan can draw them. */
  group: number[];
  onGroup: (ids: number[]) => void;
  label: string;
  onLabel: (v: string) => void;
  onClose: () => void;
}

export default function PlanPicker({ pick, onPick, group, onGroup, label, onLabel, onClose }: Props) {
  const [copied, setCopied] = useState<string | null>(null);

  async function copy(text: string, what: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      window.setTimeout(() => setCopied(null), 1400);
    } catch {
      // Clipboard needs a secure context and a user gesture, and Safari is
      // fussier than the spec suggests. The text is on screen either way, so a
      // failure is not worth an error state — say nothing, let it be read.
    }
  }

  const move = (dx: number, dy: number) =>
    onPick(pickAt({ x: pick.pct.x + dx, y: pick.pct.y + dy }));

  const inGroup = pick.buildingId !== null && group.includes(pick.buildingId);
  const venueLine = `"x": ${pick.pct.x}, "y": ${pick.pct.y}`;
  const wingLine = `"${label || 'LABEL'}": [${group.join(', ')}]`;

  return (
    <div className="plan-picker">
      <div className="plan-picker-head">
        <strong>Coordinates</strong>
        <button type="button" className="btn btn-icon btn-secondary" onClick={onClose} aria-label="Close">
          <span aria-hidden="true">×</span>
        </button>
      </div>

      <div className="plan-picker-xy">
        <label>
          x
          <input
            type="number"
            step="0.5"
            min="0"
            max="100"
            value={pick.pct.x}
            onChange={(e) => onPick(pickAt({ x: Number(e.target.value), y: pick.pct.y }))}
          />
        </label>
        <label>
          y
          <input
            type="number"
            step="0.5"
            min="0"
            max="100"
            value={pick.pct.y}
            onChange={(e) => onPick(pickAt({ x: pick.pct.x, y: Number(e.target.value) }))}
          />
        </label>
        {/* Nudge by a quarter of a percent — just under a metre either way, which
            is finer than the gap between two blocks. */}
        <span className="plan-picker-nudge">
          <button type="button" className="btn btn-secondary btn-tiny" onClick={() => move(-0.25, 0)} aria-label="Left">←</button>
          <button type="button" className="btn btn-secondary btn-tiny" onClick={() => move(0.25, 0)} aria-label="Right">→</button>
          <button type="button" className="btn btn-secondary btn-tiny" onClick={() => move(0, -0.25)} aria-label="Seaward">↑</button>
          <button type="button" className="btn btn-secondary btn-tiny" onClick={() => move(0, 0.25)} aria-label="Inland">↓</button>
        </span>
      </div>

      <dl className="plan-picker-rows">
        <dt>pin</dt>
        <dd>
          <code>{venueLine}</code>
          <button type="button" className="btn btn-secondary btn-tiny" onClick={() => copy(venueLine, 'v')}>
            {copied === 'v' ? 'Copied' : 'Copy'}
          </button>
          <span className="plan-picker-none">
            {pick.metres.x} N, {pick.metres.y} E
          </span>
        </dd>

        <dt>here</dt>
        <dd>
          {pick.buildingId === null ? (
            <span className="plan-picker-none">open ground</span>
          ) : (
            <>
              <code>{pick.buildingId}</code>
              {pick.buildingName && <span className="plan-picker-none">{pick.buildingName}</span>}
              <button
                type="button"
                className="btn btn-secondary btn-tiny"
                onClick={() =>
                  onGroup(
                    inGroup
                      ? group.filter((g) => g !== pick.buildingId)
                      : [...group, pick.buildingId as number],
                  )
                }
              >
                {inGroup ? 'Remove from wing' : 'Add to wing'}
              </button>
            </>
          )}
        </dd>

        <dt>wing</dt>
        <dd>
          <input
            className="plan-picker-label"
            value={label}
            onChange={(e) => onLabel(e.target.value)}
            placeholder="label, e.g. 3"
            aria-label="Wing label"
          />
          <span className="plan-picker-none">
            {group.length === 0 ? 'empty' : `${group.length} building${group.length > 1 ? 's' : ''}`}
          </span>
        </dd>

        {group.length > 0 && (
          <>
            <dt>paste</dt>
            <dd>
              <code>{wingLine}</code>
              <button type="button" className="btn btn-secondary btn-tiny" onClick={() => copy(wingLine, 'w')}>
                {copied === 'w' ? 'Copied' : 'Copy'}
              </button>
              <button type="button" className="btn btn-secondary btn-tiny" onClick={() => onGroup([])}>
                Clear
              </button>
            </dd>
          </>
        )}
      </dl>

      <p className="plan-picker-hint">
        x along the beach (0 south → 100 north), y inland (0 sea → 100 back fence). 1% is{' '}
        {(VIEW.w / 100).toFixed(1)} m across and {(VIEW.h / 100).toFixed(1)} m deep. Wings go in{' '}
        <code>venue.json</code> → <code>resort.wings</code>, then add the label to{' '}
        <code>stayWings</code>.
      </p>
    </div>
  );
}
